import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { isVendedorScopedRole } from '@/types/auth';
import { fetchEscopoIdsVendedor } from '@/lib/vendedorScope';
import { useMemo } from 'react';

export interface EventoAgenda {
  id: number;
  titulo: string;
  descricao?: string;
  data_inicio: string;
  data_fim?: string;
  tipo: 'renovacao_seguro' | 'vencimento_contrato' | 'pagamento' | 'outro';
  contrato_id?: number;
  cliente_id?: number;
  pedido_id?: number | null;
  numero_ciclo?: number | null;
  status: 'pendente' | 'concluido' | 'cancelado';
  prioridade: 'baixa' | 'media' | 'alta';
  created_at: string;
  updated_at: string;
  
  // Relacionamentos
  cliente?: {
    id: number;
    nome: string;
    email?: string;
    telefone?: string;
    cidade?: string;
    estado?: string;
  };
  contrato?: {
    id: number;
    numero_contrato: string;
    valor: number;
    pedido?: {
      id: number;
    };
  };
}

export type AgendaUrgenciaFiltro = 'proximas30' | 'urgentes7' | 'vencidas' | null;

export type AgendaFiltroParams = {
  periodo?: { dataInicio?: string; dataFim?: string } | null; // agenda.data_fim (renovação)
  status?: string | null;
  prioridade?: string | null;
  estado?: string | null; // via cliente
  cidade?: string | null; // via cliente
  /** Atalho de janela de vencimento (pendentes) */
  urgencia?: AgendaUrgenciaFiltro;
  /** Restringe às renovações do vendedor (contratos/pedidos do escopo) */
  vendedorId?: string | null;
} | null;

// Hook para contar total de eventos da agenda
export function useAgendaCount() {
  return useQuery({
    queryKey: ['agenda', 'count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('agenda')
        .select('*', { count: 'exact', head: true });

      if (error) {
        console.error('❌ Erro ao contar eventos da agenda:', error);
        throw error;
      }

      return count || 0;
    },
    staleTime: 60 * 1000, // 1 minuto - contagem muda menos frequentemente
    refetchOnWindowFocus: false,
  });
}

export type AgendaEstatisticas = {
  total: number;
  proximas30: number;
  urgentes7: number;
  pendentes: number;
};

/** Contagens reais do dash (não usa só a página atual da listagem). */
export function useAgendaEstatisticas() {
  const { user, profile } = useAuth();
  const isScoped = isVendedorScopedRole(profile?.role) && !!user?.id;

  return useQuery({
    queryKey: ['agenda', 'estatisticas', isScoped ? user?.id : 'all'],
    queryFn: async (): Promise<AgendaEstatisticas> => {
      let escopoContratoIds: number[] | null = null;
      if (isScoped && user?.id) {
        const { contratoIds } = await fetchEscopoIdsVendedor(user.id);
        if (contratoIds.length === 0) {
          return { total: 0, proximas30: 0, urgentes7: 0, pendentes: 0 };
        }
        escopoContratoIds = contratoIds;
      }

      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      const em7 = new Date(hoje);
      em7.setDate(hoje.getDate() + 7);
      const em30 = new Date(hoje);
      em30.setDate(hoje.getDate() + 30);
      const hojeStr = hoje.toISOString().split('T')[0];
      const em7Str = em7.toISOString().split('T')[0];
      const em30Str = em30.toISOString().split('T')[0];

      const baseCount = () => {
        let q = supabase
          .from('agenda')
          .select('*', { count: 'exact', head: true })
          .eq('tipo', 'renovacao_seguro');
        if (escopoContratoIds !== null) {
          q = q.in('contrato_id', escopoContratoIds);
        }
        return q;
      };

      const [totalRes, proximasRes, urgentesRes, pendentesRes] = await Promise.all([
        baseCount(),
        baseCount()
          .eq('status', 'pendente')
          .not('data_fim', 'is', null)
          .gte('data_fim', hojeStr)
          .lte('data_fim', em30Str),
        baseCount()
          .eq('status', 'pendente')
          .not('data_fim', 'is', null)
          .gte('data_fim', hojeStr)
          .lte('data_fim', em7Str),
        baseCount().eq('status', 'pendente'),
      ]);

      if (totalRes.error) throw totalRes.error;
      if (proximasRes.error) throw proximasRes.error;
      if (urgentesRes.error) throw urgentesRes.error;
      if (pendentesRes.error) throw pendentesRes.error;

      return {
        total: totalRes.count || 0,
        proximas30: proximasRes.count || 0,
        urgentes7: urgentesRes.count || 0,
        pendentes: pendentesRes.count || 0,
      };
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Hook para buscar eventos da agenda paginados
export function useAgendaPaginated(
  page: number = 1, 
  limit: number = 15, 
  searchTerm?: string,
  filtroRenovacoesProximas: boolean = false,
  filtros?: AgendaFiltroParams
) {
  const { user, profile } = useAuth();
  const isScoped = isVendedorScopedRole(profile?.role) && !!user?.id;
  const filtrosComEscopo = useMemo(() => {
    if (!isScoped) return filtros ?? null;
    return { ...(filtros || {}), vendedorId: user!.id };
  }, [filtros, isScoped, user?.id]);

  const urgenciaEfetiva: AgendaUrgenciaFiltro =
    filtrosComEscopo?.urgencia ?? (filtroRenovacoesProximas ? 'proximas30' : null);

  return useQuery({
    queryKey: ['agenda', 'paginated', page, limit, searchTerm, urgenciaEfetiva, filtrosComEscopo ?? null],
    queryFn: async () => {
      console.log(`🔍 Buscando agenda - Página ${page}, Limite ${limit}, Busca: ${searchTerm || 'nenhuma'}, Urgência: ${urgenciaEfetiva || 'todas'}...`);
      
      try {
        const from = (page - 1) * limit;
        const to = from + limit - 1;

        let escopoContratoIds: number[] | null = null;
        if (filtrosComEscopo?.vendedorId) {
          const { contratoIds } = await fetchEscopoIdsVendedor(filtrosComEscopo.vendedorId);
          if (contratoIds.length === 0) {
            return { data: [] as EventoAgenda[], count: 0 };
          }
          escopoContratoIds = contratoIds;
        }

        const clienteFilterPresent = !!(
          (filtrosComEscopo?.estado && filtrosComEscopo.estado.trim()) ||
          (filtrosComEscopo?.cidade && filtrosComEscopo.cidade.trim())
        );

        // Lista somente renovações de seguro (eventos de pagamento ficam no Financeiro)
        let query = supabase
          .from('agenda')
          .select(`
            *,
            cliente:clientes${clienteFilterPresent ? '!inner' : ''}(id, nome, cidade, estado),
            contrato:contratos(id, numero_contrato, pedido:pedidos(id))
          `, { count: 'exact' })
          .eq('tipo', 'renovacao_seguro');

        if (escopoContratoIds !== null) {
          query = query.in('contrato_id', escopoContratoIds);
        }

        // Filtros adicionais
        if (filtrosComEscopo?.periodo?.dataInicio) query = query.gte("data_fim", filtrosComEscopo.periodo.dataInicio);
        if (filtrosComEscopo?.periodo?.dataFim) query = query.lte("data_fim", filtrosComEscopo.periodo.dataFim);
        if (filtrosComEscopo?.status && filtrosComEscopo.status.trim() && filtrosComEscopo.status !== "todos") query = query.eq("status", filtrosComEscopo.status.trim());
        if (filtrosComEscopo?.prioridade && filtrosComEscopo.prioridade.trim() && filtrosComEscopo.prioridade !== "todas") query = query.eq("prioridade", filtrosComEscopo.prioridade.trim());
        if (filtrosComEscopo?.estado && filtrosComEscopo.estado.trim()) query = query.eq("cliente.estado", filtrosComEscopo.estado.trim().toUpperCase());
        if (filtrosComEscopo?.cidade && filtrosComEscopo.cidade.trim()) query = query.eq("cliente.cidade", filtrosComEscopo.cidade.trim());

        // Atalhos de urgência (janela de vencimento)
        if (urgenciaEfetiva) {
          const hoje = new Date();
          hoje.setHours(0, 0, 0, 0);
          const hojeStr = hoje.toISOString().split('T')[0];
          if (urgenciaEfetiva === 'vencidas') {
            query = query.not('data_fim', 'is', null).lt('data_fim', hojeStr);
          } else {
            const dias = urgenciaEfetiva === 'urgentes7' ? 7 : 30;
            const dataLimite = new Date(hoje);
            dataLimite.setDate(hoje.getDate() + dias);
            const dataLimiteStr = dataLimite.toISOString().split('T')[0];
            query = query
              .not('data_fim', 'is', null)
              .gte('data_fim', hojeStr)
              .lte('data_fim', dataLimiteStr);
          }
        }

        // Aplicar busca se fornecido
        if (searchTerm && searchTerm.trim()) {
          const searchLower = searchTerm.trim().toLowerCase();
          
          // Se for número, buscar por ID do evento
          if (!isNaN(Number(searchTerm.trim()))) {
            const idSearch = parseInt(searchTerm.trim());
            query = query.eq('id', idSearch);
          } else {
            // Se for texto, buscar em campos diretos e também em clientes relacionados
            const { data: clientesEncontrados } = await supabase
              .from('clientes')
              .select('id')
              .ilike('nome', `%${searchLower}%`);
            
            if (clientesEncontrados && clientesEncontrados.length > 0) {
              const clienteIds = clientesEncontrados.map(c => c.id);
              query = query.or(`titulo.ilike.%${searchLower}%,descricao.ilike.%${searchLower}%,tipo.ilike.%${searchLower}%,status.ilike.%${searchLower}%,cliente_id.in.(${clienteIds.join(',')})`);
            } else {
              query = query.or(`titulo.ilike.%${searchLower}%,descricao.ilike.%${searchLower}%,tipo.ilike.%${searchLower}%,status.ilike.%${searchLower}%`);
            }
          }
        }

        // Ordenar por data_fim crescente: mais próximas do vencimento no topo
        query = query.order('data_fim', { ascending: true });
        
        query = query.range(from, to);

        const { data, error, count } = await query;

        if (error) {
          console.error('❌ Erro ao buscar agenda:', error);
          throw error;
        }

        console.log(`✅ Eventos da agenda encontrados na página ${page}:`, data?.length || 0, `Total: ${count || 0}`);
        return { data: ((data || []) as unknown) as EventoAgenda[], count: count || 0 };
      } catch (error) {
        console.error('💥 Erro geral na busca da agenda:', error);
        throw error;
      }
    },
    staleTime: 2 * 60 * 1000, // 2 minutos - agenda pode ser atualizada mais frequentemente
    refetchOnWindowFocus: false, // Desabilitado para reduzir egresso
    gcTime: 5 * 60 * 1000, // 5 minutos de cache
  });
}

/**
 * Renovações para o Dashboard:
 * - Se houver eventos vencendo em até 30 dias, retorna somente esses (mais urgentes)
 * - Caso contrário, retorna os N mais próximos (para não deixar em branco)
 * Independente de contrato (contrato_id pode ser nulo).
 */
export function useRenovacoesSeguroDashboard(limit: number = 5) {
  return useQuery({
    queryKey: ['agenda', 'renovacoes-dashboard', limit],
    queryFn: async () => {
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      const dataLimite = new Date(hoje);
      dataLimite.setDate(dataLimite.getDate() + 30);
      dataLimite.setHours(23, 59, 59, 999);

      const hojeStr = hoje.toISOString().split('T')[0];
      const dataLimiteStr = dataLimite.toISOString().split('T')[0];

      const selectStr = `
        *,
        cliente:clientes(id, nome),
        contrato:contratos(id, numero_contrato, valor)
      `;

      // 1) Prioridade: até 30 dias
      const { data: urgentes, error: urgentesError } = await supabase
        .from('agenda')
        .select(selectStr)
        .eq('tipo', 'renovacao_seguro')
        .eq('status', 'pendente')
        .not('data_fim', 'is', null)
        .gte('data_fim', hojeStr)
        .lte('data_fim', dataLimiteStr)
        .order('data_fim', { ascending: true })
        .limit(limit);

      if (urgentesError) throw urgentesError;
      if (urgentes && urgentes.length > 0) {
        return { data: urgentes as EventoAgenda[], isFallback: false };
      }

      // 2) Fallback: próximos (mesmo > 30 dias)
      const { data: proximos, error: proximosError } = await supabase
        .from('agenda')
        .select(selectStr)
        .eq('tipo', 'renovacao_seguro')
        .eq('status', 'pendente')
        .not('data_fim', 'is', null)
        .gte('data_fim', hojeStr)
        .order('data_fim', { ascending: true })
        .limit(limit);

      if (proximosError) throw proximosError;
      return { data: (proximos || []) as EventoAgenda[], isFallback: true };
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export { calcularDiasAteVencimento } from "@/lib/renovacaoPrazo";

// Função helper para obter cor e label de urgência
export function getUrgenciaInfo(dias: number | null): { cor: string; label: string; bgColor: string; textColor: string } {
  if (dias === null) {
    return { cor: 'gray', label: 'Sem data', bgColor: 'bg-gray-100', textColor: 'text-gray-800' };
  }
  
  if (dias < 0) {
    return { cor: 'red', label: 'Vencido', bgColor: 'bg-red-100', textColor: 'text-red-800' };
  }
  
  if (dias <= 7) {
    return { cor: 'red', label: 'Urgente', bgColor: 'bg-red-100', textColor: 'text-red-800' };
  }
  
  if (dias <= 15) {
    return { cor: 'orange', label: 'Atenção', bgColor: 'bg-orange-100', textColor: 'text-orange-800' };
  }
  
  if (dias <= 30) {
    return { cor: 'yellow', label: 'Próximo', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' };
  }
  
  return { cor: 'green', label: 'Normal', bgColor: 'bg-green-100', textColor: 'text-green-800' };
}

// Hook legado - mantido para compatibilidade, mas agora usa paginação
export function useAgenda() {
  return useAgendaPaginated(1, 15);
}

export function useCriarEventoAgenda() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventoData: {
      titulo: string;
      descricao?: string;
      data_inicio: string;
      data_fim?: string;
      tipo: 'renovacao_seguro' | 'vencimento_contrato' | 'pagamento' | 'outro';
      contrato_id?: number;
      cliente_id?: number;
      pedido_id?: number;
      numero_ciclo?: number;
      status?: 'pendente' | 'concluido' | 'cancelado';
      prioridade?: 'baixa' | 'media' | 'alta';
    }) => {
      const dadosParaInserir = {
        ...eventoData,
        status: eventoData.status || 'pendente',
        prioridade: eventoData.prioridade || 'media'
      };

      console.log('Criando evento na agenda:', dadosParaInserir);

      const { data, error } = await supabase
        .from('agenda')
        .insert(dadosParaInserir)
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar evento na agenda:', error);
        throw error;
      }

      console.log('Evento criado na agenda:', data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agenda'] });
    }
  });
}

export function useAtualizarEventoAgenda() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updateData }: { 
      id: number; 
      titulo?: string;
      descricao?: string;
      data_inicio?: string;
      data_fim?: string;
      status?: 'pendente' | 'concluido' | 'cancelado';
      prioridade?: 'baixa' | 'media' | 'alta';
    }) => {
      const { data, error } = await supabase
        .from('agenda')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agenda'] });
    }
  });
}

// Função para criar eventos de renovação automaticamente
export function useCriarRenovacaoAutomatica() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contratoData: {
      contrato_id: number;
      cliente_id: number;
      data_fim: string;
      valor: number;
    }) => {
      // Criar evento de renovação 30 dias antes do vencimento
      const dataRenovacao = new Date(contratoData.data_fim);
      dataRenovacao.setDate(dataRenovacao.getDate() - 30);

      // Buscar nome do cliente
      const { data: clienteData } = await supabase
        .from('clientes')
        .select('nome')
        .eq('id', contratoData.cliente_id)
        .single();

      const nomeCliente = clienteData?.nome || 'Cliente não encontrado';

      const { data: contratoRow } = await supabase
        .from('contratos')
        .select('pedido_id, numero_ciclo')
        .eq('id', contratoData.contrato_id)
        .maybeSingle();

      let pedidoId = contratoRow?.pedido_id ? Number(contratoRow.pedido_id) : undefined;
      let numeroCiclo = Number(contratoRow?.numero_ciclo) || 1;

      if (pedidoId) {
        const { data: pedidoData } = await supabase
          .from('pedidos')
          .select('id, numero_ciclo')
          .eq('id', pedidoId)
          .maybeSingle();
        if (pedidoData?.numero_ciclo) numeroCiclo = Number(pedidoData.numero_ciclo);
      }

      if (pedidoId) {
        const { data: existente } = await supabase
          .from('agenda')
          .select('id')
          .eq('tipo', 'renovacao_seguro')
          .eq('status', 'pendente')
          .eq('pedido_id', pedidoId)
          .maybeSingle();
        if (existente?.id) return existente;
      }

      const eventoRenovacao = {
        titulo: `Renovação de Seguro - ${pedidoId ? `${pedidoId} - ` : ''}${nomeCliente}`,
        descricao: `Renovação do contrato ID: ${contratoData.contrato_id}`,
        data_inicio: dataRenovacao.toISOString().split('T')[0],
        data_fim: contratoData.data_fim,
        tipo: 'renovacao_seguro' as const,
        contrato_id: contratoData.contrato_id,
        cliente_id: contratoData.cliente_id,
        pedido_id: pedidoId ?? null,
        numero_ciclo: numeroCiclo,
        status: 'pendente' as const,
        prioridade: 'alta' as const
      };

      console.log('Criando evento de renovação:', eventoRenovacao);

      const { data, error } = await supabase
        .from('agenda')
        .insert(eventoRenovacao)
        .select()
        .single();

      if (error) {
        if ((error as { code?: string }).code === "23505") {
          return null;
        }
        console.error('Erro ao criar evento de renovação:', error);
        throw error;
      }

      console.log('Evento de renovação criado:', data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agenda'] });
    }
  });
} 